import React, { useState } from 'react'
import { IconButton, Tooltip, CircularProgress } from '@mui/material'
import EmailIcon from '@mui/icons-material/Email'
import { useAppSelector } from '../../../../redux/store'
import { selectUserTeamMemberInfo } from '../../../../redux/auth/selector'
import { sendTaskCreationNotification, formatTaskDueDateForEmail, formatTaskDueCountdown } from '../../../../utils/emailNotificationUtils'
import showToast from '../../../Common/SweetAlert/ToastAlert'
import { EMAIL_CONFIG, isNotifyInviteesEnabled } from '../../../../config/emailConfig'
import { Task } from '../../../../redux/task/types'
import axios from '../../../../config/axios'

interface TaskEmailNotificationButtonProps {
  task: Task
}

const TaskEmailNotificationButton: React.FC<TaskEmailNotificationButtonProps> = ({ task }) => {
  const [loading, setLoading] = useState(false)
  const userInfo = useAppSelector(selectUserTeamMemberInfo)

  /**
   * Fetch assignees for this task from the API and return the emails and names of those
   * who have NOT yet completed the task (status !== 'completed').
   */
  const resolveIncompleteAssignees = async (): Promise<Array<{ email: string; name: string }>> => {
    try {
      const response = await axios.get(`/report/task/${task.id}/assignedTeamMember`)
      const data = response.data

      // data.assignedTeamMembers is the list from the backend
      const members: Array<{ email: string; taskStatus: string; firstName?: string; lastName?: string }> =
        data?.assignedTeamMembers || []

      const seen = new Set<string>()
      const incompleteAssignees: Array<{ email: string; name: string }> = []

      for (const m of members) {
        if (m.taskStatus?.toLowerCase() !== 'completed' && m.email && !seen.has(m.email)) {
          seen.add(m.email)
          const name =
            m.firstName && m.lastName
              ? `${m.firstName} ${m.lastName}`
              : m.firstName || m.email
          incompleteAssignees.push({ email: m.email, name })
        }
      }

      return incompleteAssignees
    } catch (error) {
      console.error('Failed to fetch assigned team members:', error)
      return []
    }
  }

  const handleSendEmail = async () => {
    if (!userInfo?.email) {
      showToast('error', 'User email not found', 'top-end')
      return
    }

    const creatorFullName =
      userInfo.firstName && userInfo.lastName
        ? `${userInfo.firstName} ${userInfo.lastName}`
        : userInfo.email

    const notifyAll = isNotifyInviteesEnabled()
    let recipients: Array<{ email: string; name: string }> = []
    let intendedAssignees: Array<{ email: string; name: string }> = []

    // Always fetch the actual intended recipients (non-completed assignees)
    intendedAssignees = await resolveIncompleteAssignees()

    if (notifyAll) {
      // Production: send to actual non-completed assignees
      recipients = intendedAssignees
      if (recipients.length === 0) {
        showToast('info', 'All assignees have already completed this task.', 'top-end')
        return
      }
    } else {
      // Dev/Test/Staging: always send to current user regardless of assignee status
      recipients = [{ email: userInfo.email, name: creatorFullName }]
    }

    setLoading(true)

    try {

      const formattedDueDate = formatTaskDueDateForEmail(task.dueDate)
      const taskDueCountdown = formatTaskDueCountdown(task.dueDate)

      // Build a dev note when not in production mode
      const intendedNote = intendedAssignees.length > 0
        ? `Intended recipients (${intendedAssignees.length}): ${intendedAssignees.map((a) => a.email).join(', ')}`
        : 'No incomplete assignees found for this task.'
      const devNote = !notifyAll
        ? `[DEV/TEST MODE] This email was redirected to you (${userInfo.email}) instead of the actual recipients. ${intendedNote}`
        : undefined

      const emailPromises = recipients.map(({ email: recipientEmail, name: recipientName }) =>
        sendTaskCreationNotification(
          {
            to: recipientEmail,
            subject: EMAIL_CONFIG.SUBJECTS.TASK_REMINDER(),
            template: EMAIL_CONFIG.TEMPLATES.TASK_REMINDER,
            taskTitle: task.title,
            taskDetails: task.details,
            taskDueDate: formattedDueDate,
            taskDueCountdown,
            taskImportance: task.importance,
            taskLink: task.link,
            taskAppUrl: `${window.location.origin}/tasks`,
            creatorName: creatorFullName,
            recipientName,
            taskId: task.id,
            devNote,
          },
          (_failureMessage: string) => {
            console.warn(`📧 Task email failed for: ${recipientEmail}`)
          },
        ),
      )

      await Promise.all(emailPromises)

      const count = recipients.length
      showToast('success', `Email notification sent to ${count} ${count === 1 ? 'recipient' : 'recipients'}!`, 'top-end')
    } catch (error) {
      console.error('Failed to send task email notifications:', error)
      showToast('error', 'Failed to send email notifications', 'top-end')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Tooltip title='Send Email' arrow>
      <IconButton
        onClick={handleSendEmail}
        disabled={loading}
        size='small'
        sx={{
          color: '#1976d2',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
          },
          '&:disabled': {
            color: 'rgba(0, 0, 0, 0.26)',
          },
        }}
      >
        {loading ? <CircularProgress size={20} /> : <EmailIcon fontSize='small' />}
      </IconButton>
    </Tooltip>
  )
}

export default TaskEmailNotificationButton
